import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAnalyticsAPI } from '../lib/api';
import { BarChart3, TrendingUp, Eye, MousePointer, Star, Calendar, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#1A73E8', '#1E8E3E', '#F9AB00', '#D93025', '#9334E9'];

const DATE_RANGES = [
  { key: '1w', label: '1 Week' },
  { key: '1m', label: '1 Month' },
  { key: '3m', label: '3 Months' },
  { key: '6m', label: '6 Months' },
  { key: '1y', label: '1 Year' },
  { key: 'custom', label: 'Custom' },
];

const AnalyticsDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('1m');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const fetchData = useCallback(async (range, start, end) => {
    setLoading(true);
    try {
      const res = await dashboardAnalyticsAPI.get(range, start, end);
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData(dateRange);
  }, []);

  const handleRangeChange = (key) => {
    setDateRange(key);
    if (key === 'custom') {
      setShowCustomPicker(true);
      return;
    }
    setShowCustomPicker(false);
    fetchData(key);
  };

  const handleCustomApply = () => {
    if (!customStart || !customEnd) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (customStart > customEnd) {
      toast.error('Start date must be before end date');
      return;
    }
    fetchData('custom', customStart, customEnd);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, post_performance, review_trends, profile_views, customer_actions, rating_distribution, is_real_data } = data;

  const ratingData = Object.entries(rating_distribution).map(([star, count]) => ({
    name: `${star} Star`, value: count, star: parseInt(star)
  })).reverse();

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header + Date Range */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Analytics</h1>
          <p className="text-[#5F6368]">Performance insights for your business profile</p>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="dashboard-card">
        <div className="flex items-center gap-3 flex-wrap">
          <Calendar className="w-5 h-5 text-[#5F6368]" />
          <div className="flex items-center gap-1 bg-[#F1F3F4] rounded-lg p-1">
            {DATE_RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => handleRangeChange(r.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  dateRange === r.key
                    ? 'bg-[#1A73E8] text-white shadow-sm'
                    : 'text-[#5F6368] hover:text-[#202124] hover:bg-white'
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {loading && <div className="animate-spin w-4 h-4 border-2 border-[#1A73E8] border-t-transparent rounded-full" />}
        </div>

        {/* Custom Date Picker */}
        {showCustomPicker && (
          <div className="mt-4 flex items-end gap-3 flex-wrap" data-testid="custom-date-picker">
            <div>
              <label className="text-xs text-[#5F6368] block mb-1">Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input-google text-sm"
                data-testid="custom-start-date"
              />
            </div>
            <div>
              <label className="text-xs text-[#5F6368] block mb-1">End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input-google text-sm"
                data-testid="custom-end-date"
              />
            </div>
            <button
              onClick={handleCustomApply}
              className="btn-google text-sm px-4 py-2"
              data-testid="apply-custom-range"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Real vs Demo Data Notice */}
      {!is_real_data && (
        <div className="flex items-center gap-2 bg-[#E8F0FE] rounded-lg px-4 py-2.5" data-testid="demo-data-notice">
          <Info className="w-4 h-4 text-[#1A73E8] flex-shrink-0" />
          <p className="text-sm text-[#1A73E8]">
            Showing simulated data. Connect Google Business Profile and enable the Performance API for real metrics.
          </p>
        </div>
      )}
      {is_real_data && (
        <div className="flex items-center gap-2 bg-[#E6F4EA] rounded-lg px-4 py-2.5" data-testid="real-data-notice">
          <Info className="w-4 h-4 text-[#1E8E3E] flex-shrink-0" />
          <p className="text-sm text-[#1E8E3E]">Showing real data from Google Business Profile Performance API.</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="dashboard-card" data-testid="stat-total-views">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#1A73E8]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368]">Total Views</p>
              <p className="text-lg font-semibold text-[#202124]">{summary.total_views.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-total-clicks">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
              <MousePointer className="w-5 h-5 text-[#1E8E3E]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368]">Total Clicks</p>
              <p className="text-lg font-semibold text-[#202124]">{summary.total_clicks.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-avg-rating">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-[#F9AB00]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368]">Avg Rating</p>
              <p className="text-lg font-semibold text-[#202124]">{summary.avg_rating}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-posts">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F3E8FD] rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#9334E9]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368]">Posts Created</p>
              <p className="text-lg font-semibold text-[#202124]">{summary.total_posts}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-replies">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FCE8E6] rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-[#D93025]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368]">Replies Sent</p>
              <p className="text-lg font-semibold text-[#202124]">{summary.total_replies}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Row 1: Post Performance + Profile Views */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dashboard-card" data-testid="chart-post-performance">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Post Performance</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={post_performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADCE0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#5F6368' }} interval={Math.max(Math.floor(post_performance.length / 8), 0)} />
              <YAxis tick={{ fontSize: 11, fill: '#5F6368' }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DADCE0', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="views" stroke="#1A73E8" strokeWidth={2} dot={false} name="Views" />
              <Line type="monotone" dataKey="clicks" stroke="#1E8E3E" strokeWidth={2} dot={false} name="Clicks" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card" data-testid="chart-profile-views">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Profile Views</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={profile_views}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADCE0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#5F6368' }} interval={Math.max(Math.floor(profile_views.length / 8), 0)} />
              <YAxis tick={{ fontSize: 11, fill: '#5F6368' }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DADCE0', fontSize: 12 }} />
              <Area type="monotone" dataKey="views" stroke="#9334E9" fill="#F3E8FD" strokeWidth={2} name="Views" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Customer Actions + Rating Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dashboard-card" data-testid="chart-customer-actions">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Customer Actions</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={customer_actions}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADCE0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#5F6368' }} />
              <YAxis tick={{ fontSize: 11, fill: '#5F6368' }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DADCE0', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="website_clicks" fill="#1A73E8" name="Website Clicks" radius={[4, 4, 0, 0]} />
              <Bar dataKey="phone_calls" fill="#1E8E3E" name="Phone Calls" radius={[4, 4, 0, 0]} />
              <Bar dataKey="directions" fill="#F9AB00" name="Directions" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card" data-testid="chart-rating-distribution">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Rating Distribution</h3>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="50%" height={200}>
              <PieChart>
                <Pie data={ratingData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                  {ratingData.map((entry, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DADCE0', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {ratingData.map((item, i) => (
                <div key={item.star} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i] }} />
                  <div className="flex items-center gap-1 flex-1">
                    <span className="text-sm text-[#5F6368] w-12">{item.name}</span>
                    <div className="flex-1 h-2 bg-[#F1F3F4] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min((item.value / Math.max(...ratingData.map(r => r.value))) * 100, 100)}%`, background: COLORS[i] }} />
                    </div>
                    <span className="text-sm font-medium text-[#202124] w-8 text-right">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Review Trends */}
      <div className="dashboard-card" data-testid="chart-review-trends">
        <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Review Trends</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={review_trends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#DADCE0" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#5F6368' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#5F6368' }} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fontSize: 11, fill: '#5F6368' }} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DADCE0', fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="reviews" fill="#1A73E8" name="Reviews" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="avg_rating" stroke="#F9AB00" strokeWidth={2} name="Avg Rating" dot={{ fill: '#F9AB00', r: 4 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsDashboardPage;
